/* Notification Center — the Escalation rules and labels, shared by the Escalation fields
 * (EscalationFields.tsx), Rule Detail and the New Rule wizard. The server decides what is acted on
 * (EscalationPolicy.IsEffective) and re-checks every saved value (EscalationPolicy.Validate); these only
 * guide the user. Simplified 2026-10-24 (owner request): one sentence, shown only when it can work, and
 * the escalation goes to individual USERS ("Role - User name") instead of a Recipient Group. */
import { getRecipientUsers, type RecipientUser } from './api';
import { groupUserName, type NcEscalateAfter, type NcRecipientGroup } from './data';
import type { DeliveryValue } from './deliveryMethod';

/** What the Escalation fields edit. `userIds` = the users it goes to (AI_NotificationRuleEscalationUser).
 *  `groupId` = a Recipient Group set up before users could be chosen — kept, and still escalated to, until
 *  users replace it ('' = none). */
export interface EscalationValue { userIds: string[]; groupId: string; after: NcEscalateAfter }

/** A new rule's escalation: none, and 1 hour once users are chosen (the column default). */
export const NEW_RULE_ESCALATION: EscalationValue = { userIds: [], groupId: '', after: '1h' };

/** "Escalate after" as shown ⇄ as stored (AI_NotificationRule.EscalateAfter). "End of business day" was retired
 *  2026-10-24, so "within X" always means exactly X; a rule still storing it is read as 4 hours (api.ts). */
export const ESCALATE_AFTER_LABEL: Record<NcEscalateAfter, string> = {
  '30m': '30 minutes', '1h': '1 hour', '4h': '4 hours',
};

/** Escalation can only work with In-App (bell) + Requires acknowledgment — acknowledging in the bell is what
 *  cancels it — and without a Daily digest delay. The fields are HIDDEN otherwise (no greyed controls). */
export function escAvailable(d: Pick<DeliveryValue, 'inApp' | 'ack' | 'delay'>): boolean {
  return d.inApp && d.ack && d.delay !== 'Daily digest';
}

/** Whether the rule escalates to anyone (users, or a group from before users could be chosen). */
export function escIsOn(esc: EscalationValue): boolean {
  return esc.userIds.length > 0 || !!esc.groupId;
}

// ── The active users (live Users data source, GET recipient-users), loaded once and shared ──
let usersLoaded: RecipientUser[] | null = null;
let usersPending: Promise<RecipientUser[]> | null = null;
/** Every active user, fetched once per page load (the Escalation fields call it when they appear). */
export function loadEscalationUsers(): Promise<RecipientUser[]> {
  if (usersLoaded) return Promise.resolve(usersLoaded);
  usersPending ??= getRecipientUsers()
    .then((rows) => (usersLoaded = rows))
    .finally(() => { usersPending = null; });
  return usersPending;
}
/** The users loaded so far (empty until the Escalation fields have loaded them) — for history / review text. */
export function peekEscalationUsers(): RecipientUser[] { return usersLoaded ?? []; }

/** A user as the picker and the texts show them: "Role - User name" (every role, comma-joined). */
export function escUserLabel(u: Pick<RecipientUser, 'roles' | 'name' | 'userName'>): string {
  return `${u.roles?.trim() || 'No role'} - ${groupUserName(u)}`;
}
/** The user's name only, for the one-line summary. A user no longer active shows as "an inactive user". */
function escUserName(id: string, users: RecipientUser[]): string {
  const u = users.find((x) => x.userId === id);
  return u ? groupUserName(u) : 'an inactive user';
}
/** "A", "A and B", "A, B and C", "A, B and 3 more". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length > 3) return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The chosen group's CURRENT name ('No escalation' for none, '(deleted group)' when it is gone). */
export function escGroupName(id: string, groups: Pick<NcRecipientGroup, 'id' | 'name'>[]): string {
  if (!id) return 'No escalation';
  return groups.find((g) => g.id === id)?.name ?? '(deleted group)';
}

/** Who it goes to, for history / review: the users' "Role - User name", else the legacy group, else "No escalation". */
export function escTargetText(esc: EscalationValue, users: RecipientUser[], groups: Pick<NcRecipientGroup, 'id' | 'name'>[]): string {
  if (esc.userIds.length) {
    return esc.userIds.map((id) => {
      const u = users.find((x) => x.userId === id);
      return u ? escUserLabel(u) : 'Inactive user';
    }).join(', ');
  }
  return escGroupName(esc.groupId, groups);
}

/** The one line under the Escalation sentence saying exactly what will happen. */
export function escNote(esc: EscalationValue, users: RecipientUser[], groups: Pick<NcRecipientGroup, 'id' | 'name'>[],
  d: Pick<DeliveryValue, 'email' | 'bizHours'>): string {
  const who = esc.userIds.length
    ? joinNames(esc.userIds.map((id) => escUserName(id, users)))
    : esc.groupId ? `The group “${escGroupName(esc.groupId, groups)}”` : '';
  if (!who) return 'Choose who to send it to — nothing is escalated until you do.';
  return `${who} will get it once more as “Escalated: …” by bell${d.email ? ' and email' : ''}. `
    + 'Acknowledging any copy cancels it.'
    + (d.bizHours ? ' Outside business hours it waits for the next opening.' : '');
}

/** Review / history text for the whole setting, e.g. "QA Manager - Jane Doe after 4 hours". */
export function escSummary(esc: EscalationValue, users: RecipientUser[], groups: Pick<NcRecipientGroup, 'id' | 'name'>[]): string {
  return escIsOn(esc) ? `${escTargetText(esc, users, groups)} after ${ESCALATE_AFTER_LABEL[esc.after]}` : 'No escalation';
}
